import * as React from 'react';
import { Label, type LabelProps, ChevronDown } from '@components/atoms';
import { cn } from '@utils';

export interface ICollapsibleSectionProps {
	label: string | React.ReactNode;
	labelProps?: Partial<LabelProps>;
	defaultCollapsed?: boolean;
	isCollapsed?: boolean;
	onToggle?: () => void;
	children: React.ReactNode;
	disabled?: boolean;
	className?: string;
	contentClassName?: string;
	ariaControls?: string;
	'data-testid'?: string;
}

const COLLAPSIBLE_CONTENT_ID = 'collapsible-section-content';

export const CollapsibleSection: React.FC<ICollapsibleSectionProps> = ({
	label,
	labelProps,
	defaultCollapsed = true,
	isCollapsed: isCollapsedControlled,
	onToggle,
	children,
	disabled = false,
	className,
	contentClassName,
	ariaControls = COLLAPSIBLE_CONTENT_ID,
	'data-testid': dataTestId = 'collapsible-section',
}) => {
	const [internalCollapsed, setInternalCollapsed] = React.useState(defaultCollapsed);
	const isControlled = isCollapsedControlled !== undefined;
	const isCollapsed = isControlled ? isCollapsedControlled : internalCollapsed;

	const handleToggle = React.useCallback(() => {
		if (disabled) return;
		if (isControlled) {
			onToggle?.();
		} else {
			setInternalCollapsed(prev => !prev);
			onToggle?.();
		}
	}, [disabled, isControlled, onToggle]);

	return (
		<div className={cn('flex min-w-0 flex-col', className)} data-testid={dataTestId}>
			<button
				type="button"
				id={ariaControls}
				aria-expanded={!isCollapsed}
				aria-controls={ariaControls}
				disabled={disabled}
				onClick={handleToggle}
				className={cn(
					'flex w-full min-w-0 cursor-pointer appearance-none items-center justify-start gap-1 border-0 bg-transparent p-0 text-left text-inherit',
					disabled && 'cursor-not-allowed opacity-70'
				)}
			>
				<div
					className={cn(
						'shrink-0 transition-transform duration-100 ease-in-out',
						isCollapsed && '-rotate-90'
					)}
					aria-hidden
				>
					<ChevronDown />
				</div>
				<Label htmlFor={ariaControls} label={label} {...labelProps} />
			</button>
			{!isCollapsed && (
				<div
					className={cn('min-w-0 pt-2 duration-200 animate-in fade-in-0', contentClassName)}
					data-testid={`${dataTestId}-content`}
				>
					{children}
				</div>
			)}
		</div>
	);
};

CollapsibleSection.displayName = 'CollapsibleSection';
